import React, { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { FileText, Loader2, RefreshCw, Plus, X, AlertCircle, Users, Sparkles, Edit2, Check } from 'lucide-react';
import { useWizardStore, type QuestionData } from '../../stores/wizardStore';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import Input from '../../components/ui/Input';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { api } from '../../lib/api';
import { toast } from 'sonner';
import SuggestedThemeChips from '../../components/wizard/SuggestedThemeChips';

interface Step4Props {
  onNext: () => void;
  onBack: () => void;
}

const PERSONAS = [
  { value: 'CEO', label: 'CEO (Chief Executive Officer)', icon: '👔' },
  { value: 'CFO', label: 'CFO (Chief Financial Officer)', icon: '💰' },
  { value: 'COO', label: 'COO (Chief Operating Officer)', icon: '⚙️' },
  { value: 'CTO', label: 'CTO (Chief Technology Officer)', icon: '💻' },
  { value: 'CMO', label: 'CMO (Chief Marketing Officer)', icon: '📢' },
  { value: 'CPO', label: 'CPO (Chief Product Officer)', icon: '📊' },
  { value: 'CSCO', label: 'CSCO (Chief Supply Chain Officer)', icon: '🚚' },
  { value: 'CHRO', label: 'CHRO (Chief HR Officer)', icon: '👥' },
  { value: 'CIO', label: 'CIO (Chief Information Officer)', icon: '🖥️' },
  { value: 'CDO', label: 'CDO (Chief Data Officer)', icon: '📈' },
  { value: 'CISO', label: 'CISO (Chief Security Officer)', icon: '🔒' },
  { value: 'CLO', label: 'CLO (Chief Legal Officer)', icon: '⚖️' },
];

const CATEGORIES = ['Strategy', 'Operations', 'Technology', 'Market', 'Finance', 'Risk'];
const PRIORITIES: Array<'Critical' | 'High' | 'Medium'> = ['Critical', 'High', 'Medium'];

/**
 * Compute mean gross margin / operating margin / revenue growth across the
 * competitors whose Company Research is linked (those with `financials`
 * populated). Returned object's fields are `null` when no peers have data —
 * the backend treats `null` the same as the field being absent.
 */
function computePeerAverages(competitors: Array<{ financials?: any }>): {
  grossMargin: number | null;
  operatingMargin: number | null;
  revenueGrowth: number | null;
} | undefined {
  const enriched = competitors.filter((c) => !!c.financials);
  if (enriched.length === 0) return undefined;
  const mean = (key: 'gross_margin' | 'operating_margin' | 'revenue_growth'): number | null => {
    const vals = enriched
      .map((c) => c.financials?.[key])
      .filter((v): v is number => typeof v === 'number' && !Number.isNaN(v));
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  };
  const result = {
    grossMargin: mean('gross_margin'),
    operatingMargin: mean('operating_margin'),
    revenueGrowth: mean('revenue_growth'),
  };
  // Suppress entirely if no metrics resolved
  if (
    result.grossMargin === null &&
    result.operatingMargin === null &&
    result.revenueGrowth === null
  ) {
    return undefined;
  }
  return result;
}

// Color palette for the per-question provenance chips.  Keyed by the agent's
// `dataSources[].type` enum.  Each entry pairs a soft surface (50) with a
// readable text (700) and matching border (200) so the chips read as one
// family but still distinguish at a glance.
const DATA_SOURCE_PALETTE: Record<string, string> = {
  focal_financial: 'bg-blue-50 text-blue-700 border-blue-200',
  peer_avg:        'bg-slate-50 text-slate-700 border-slate-200',
  industry_metric: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  segment:         'bg-purple-50 text-purple-700 border-purple-200',
  ev_driver:       'bg-emerald-50 text-emerald-700 border-emerald-200',
  news_event:      'bg-amber-50 text-amber-700 border-amber-200',
  regulation:      'bg-rose-50 text-rose-700 border-rose-200',
  disruption:      'bg-orange-50 text-orange-700 border-orange-200',
  technology:      'bg-cyan-50 text-cyan-700 border-cyan-200',
  default:         'bg-neutral-50 text-neutral-700 border-neutral-200',
};

export const Step4StrategicQuestions: React.FC<Step4Props> = ({ onNext, onBack }) => {
  const { companyData, industryData, competitorsData, questionsData, setQuestionsData, clearQuestionsData } = useWizardStore();

  const [isGenerating, setIsGenerating] = useState(false);
  const [showManualAdd, setShowManualAdd] = useState(false);
  const [selectedPersona, setSelectedPersona] = useState<string>('all');
  const [selectedPriority, setSelectedPriority] = useState<string>('all');
  const [manualQuestion, setManualQuestion] = useState({
    personaRole: 'CEO',
    question: '',
    context: '',
    priority: 'High' as 'Critical' | 'High' | 'Medium',
    category: 'Strategy'
  });

  // Edit state for inline editing
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [editingQuestion, setEditingQuestion] = useState<{
    question: string;
    context: string;
    priority: 'Critical' | 'High' | 'Medium';
    category: string;
  } | null>(null);

  // Persona selection for generation
  const [selectedPersonasForGen, setSelectedPersonasForGen] = useState<string[]>(['CEO', 'CFO', 'CTO']);
  const [showPersonaSelection, setShowPersonaSelection] = useState(!questionsData || questionsData.length === 0);
  const [generatingForPersona, setGeneratingForPersona] = useState<string | null>(null);

  // Destructive-action confirmation. One slot drives the single <ConfirmDialog>
  // rendered at the bottom of this component — replaces window.confirm(), which
  // blocks the JS thread, can't be themed, and renders as a bare
  // "localhost:5173 says" browser chrome popup.
  const [pendingConfirm, setPendingConfirm] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
    onConfirm: () => void;
  } | null>(null);

  // Phase-D enrichment queries — provide cross-research context to the persona
  // agent so generated questions reference real disruptions/imperatives and
  // frame finances as "focal X% vs peer avg Y%". Both queries are best-effort:
  // failures are silent and the mutation still POSTs without these fields,
  // preserving the original behaviour.
  const insightsQuery = useQuery<any>({
    queryKey: ['case-context', 'industry-insights', industryData?.industryId],
    queryFn: () => api.get(`/case-discovery/industry-insights/${industryData?.industryId}`),
    enabled: !!industryData?.industryId,
    staleTime: 1000 * 60 * 5,
    retry: false,
  });
  const snapshotQuery = useQuery<any>({
    queryKey: ['case-context', 'financial-snapshot', companyData?.companyId, companyData?.name],
    queryFn: () =>
      api.get(
        `/case-discovery/companies/${encodeURIComponent(companyData?.companyId || companyData?.name || '')}/financial-snapshot`,
        companyData?.name ? { name: companyData.name } : undefined,
      ),
    enabled: !!(companyData?.companyId || companyData?.name),
    staleTime: 1000 * 60 * 5,
    retry: false,
  });

  const generateQuestions = useMutation({
    mutationFn: async () => {
      if (!companyData || !industryData) {
        throw new Error('Company and industry data required');
      }

      console.log('❓ Calling strategic questions API...', {
        companyName: companyData.name,
        industryName: industryData.name,
      });

      // Build industry context data
      const industryContextData = {
        name: industryData.name,
        classification: industryData.classification,
        marketSizeUSD: industryData.marketSizeUSD,
        marketGrowthRate: industryData.marketGrowthRate,
        valueChain: industryData.valueChain,
        technologyTrends: industryData.technologyTrends,
        regulatoryEnvironment: industryData.regulatoryEnvironment,
        commonPainPoints: industryData.commonPainPoints,
        benchmarks: industryData.benchmarks,
      };

      // Call real backend AI strategic questions endpoint
      const response = await api.post<{
        status: string;
        personas: Array<any>;
        questions: Array<{
          text: string;  // Backend returns 'text' not 'question'
          personaRole: string;
          valueNodeCategory?: string;
          priority?: 'Critical' | 'High' | 'Medium';
          rationale?: string;
          dataSources?: Array<{ type: string; label: string; value: string }>;
        }>;
        total_questions: number;
      }>('/case-discovery/strategic-questions', {
        companyName: companyData.name,
        industryName: industryData.name,
        industryData: industryContextData,
        competitorData: competitorsData.filter(c => c.selected).map(c => ({
          name: c.name,
          description: c.description,
          market_position: c.market_position,
        })),
        selectedPersonas: selectedPersonasForGen, // Pass selected personas
        // Phase-D enrichment — Optional. Backend ignores absent fields and
        // behaves identically to before, so this is purely additive.
        industryInsights: insightsQuery.data?.industryInsights ?? undefined,
        focalCompanyFinancials: snapshotQuery.data?.financials ?? undefined,
        peerAverages: computePeerAverages(competitorsData),
        // Phase-E enrichment — anchor questions in EV drivers, segments,
        // deeper benchmarks, and recent news; surfaces as citation chips
        // (dataSources[]) on each generated question.  All fields are
        // optional — read opportunistically from existing wizard state and
        // snapshot response; absent ones produce no behavioural change.
        evBreakdown: snapshotQuery.data?.evBreakdown ?? snapshotQuery.data?.ev ?? undefined,
        businessSegments:
          (companyData as any)?.segments ??
          snapshotQuery.data?.segments ??
          undefined,
        deeperBenchmarks:
          snapshotQuery.data?.deeperBenchmarks ??
          snapshotQuery.data?.extendedMetrics ??
          undefined,
        newsIntelligence:
          snapshotQuery.data?.recentNews ??
          (companyData as any)?.news ??
          undefined,
      });

      console.log('✅ Strategic questions API response:', response);
      console.log(`   Personas: ${response.personas?.length || 0}`);
      console.log(`   Questions: ${response.questions?.length || 0}`);

      // Map API response to QuestionData format
      const mappedQuestions: QuestionData[] = response.questions.map((q, index) => {
        // Normalize personaRole to uppercase to match PERSONAS values
        const normalizedRole = q.personaRole?.toUpperCase() || 'Other';
        const mapped = {
          questionId: `q_ai_${Date.now()}_${index}`,
          personaRole: normalizedRole,
          question: q.text,  // Map 'text' to 'question'
          context: q.rationale,  // Map 'rationale' to 'context'
          priority: q.priority || 'High',
          category: q.valueNodeCategory || 'Strategy',  // Map 'valueNodeCategory' to 'category'
          estimatedImpact: undefined,  // Not provided by backend
          selected: q.priority === 'Critical' || index < 10, // Auto-select critical and first 10
          // Preserve the provenance chips emitted by the persona agent so the
          // wizard can render them under each question (Phase-E citation UI).
          dataSources: Array.isArray(q.dataSources) ? q.dataSources : [],
        };

        console.log(`   Question ${index + 1}: "${mapped.question?.substring(0, 50)}..." (raw: ${q.personaRole}, normalized: ${normalizedRole})`);
        return mapped;
      });

      console.log(`✅ Mapped ${mappedQuestions.length} questions successfully`);
      return mappedQuestions;
    },
    onSuccess: (data) => {
      setQuestionsData(data);
      setIsGenerating(false);
      toast.success(`${data.length} strategic questions generated by AI`);
    },
    onError: (error: any) => {
      console.error('❌ Question generation failed:', error);
      setIsGenerating(false);
      toast.error(`Question generation failed: ${error.message || 'Unknown error'}`);
    }
  });

  const handleGenerateQuestions = () => {
    setIsGenerating(true);
    generateQuestions.mutate();
  };

  const handleToggleQuestion = (questionId: string) => {
    const updated = questionsData.map(q =>
      q.questionId === questionId ? { ...q, selected: !q.selected } : q
    );
    setQuestionsData(updated);
  };

  const handleAddManualQuestion = () => {
    if (!manualQuestion.question) return;

    const newQuestion: QuestionData = {
      questionId: `manual_q_${Date.now()}`,
      personaRole: manualQuestion.personaRole,
      question: manualQuestion.question,
      context: manualQuestion.context,
      priority: manualQuestion.priority,
      category: manualQuestion.category,
      selected: true
    };

    setQuestionsData([...questionsData, newQuestion]);
    setManualQuestion({
      personaRole: 'CEO',
      question: '',
      context: '',
      priority: 'High',
      category: 'Strategy'
    });
    setShowManualAdd(false);
  };

  const handleRemoveQuestion = (questionId: string) => {
    setQuestionsData(questionsData.filter(q => q.questionId !== questionId));
  };

  const handleStartEdit = (question: QuestionData) => {
    setEditingQuestionId(question.questionId!);
    setEditingQuestion({
      question: question.question,
      context: question.context || '',
      priority: question.priority || 'High',
      category: question.category || 'Strategy'
    });
  };

  const handleSaveEdit = (questionId: string) => {
    if (!editingQuestion) return;

    const updated = questionsData.map(q =>
      q.questionId === questionId
        ? {
            ...q,
            question: editingQuestion.question,
            context: editingQuestion.context,
            priority: editingQuestion.priority,
            category: editingQuestion.category
          }
        : q
    );
    setQuestionsData(updated);
    setEditingQuestionId(null);
    setEditingQuestion(null);
    toast.success('Question updated');
  };

  const handleCancelEdit = () => {
    setEditingQuestionId(null);
    setEditingQuestion(null);
  };

  const handleRemovePersona = (persona: string) => {
    // Remove all questions for this persona
    setQuestionsData(questionsData.filter(q => q.personaRole !== persona));
  };

  // Generate questions for a single additional persona
  const handleGenerateForPersona = async (persona: string) => {
    if (!companyData || !industryData) {
      toast.error('Company and industry data required');
      return;
    }

    setGeneratingForPersona(persona);

    try {
      console.log(`❓ Generating questions for ${persona}...`);

      // Build industry context data
      const industryContextData = {
        name: industryData.name,
        classification: industryData.classification,
        marketSizeUSD: industryData.marketSizeUSD,
        marketGrowthRate: industryData.marketGrowthRate,
        valueChain: industryData.valueChain,
        technologyTrends: industryData.technologyTrends,
        regulatoryEnvironment: industryData.regulatoryEnvironment,
        commonPainPoints: industryData.commonPainPoints,
        benchmarks: industryData.benchmarks,
      };

      // Call API for just this one persona
      const response = await api.post<{
        status: string;
        personas: Array<any>;
        questions: Array<{
          text: string;
          personaRole: string;
          valueNodeCategory?: string;
          priority?: 'Critical' | 'High' | 'Medium';
          rationale?: string;
        }>;
        total_questions: number;
      }>('/case-discovery/strategic-questions', {
        companyName: companyData.name,
        industryName: industryData.name,
        industryData: industryContextData,
        competitorData: competitorsData.filter(c => c.selected).map(c => ({
          name: c.name,
          description: c.description,
          market_position: c.market_position,
        })),
        selectedPersonas: [persona], // Only this persona
        // Phase-D + Phase-E enrichment (same as the batch call).  Optional.
        industryInsights: insightsQuery.data?.industryInsights ?? undefined,
        focalCompanyFinancials: snapshotQuery.data?.financials ?? undefined,
        peerAverages: computePeerAverages(competitorsData),
        evBreakdown: snapshotQuery.data?.evBreakdown ?? snapshotQuery.data?.ev ?? undefined,
        businessSegments:
          (companyData as any)?.segments ??
          snapshotQuery.data?.segments ??
          undefined,
        deeperBenchmarks:
          snapshotQuery.data?.deeperBenchmarks ??
          snapshotQuery.data?.extendedMetrics ??
          undefined,
        newsIntelligence:
          snapshotQuery.data?.recentNews ??
          (companyData as any)?.news ??
          undefined,
      });

      console.log(`✅ Generated ${response.questions?.length || 0} questions for ${persona}`);

      // Map API response to QuestionData format — preserve dataSources for the
      // citation chips rendered under each question.
      const newQuestions: QuestionData[] = response.questions.map((q: any, index) => ({
        questionId: `q_ai_${Date.now()}_${persona}_${index}`,
        personaRole: persona,
        question: q.text,
        context: q.rationale,
        priority: q.priority || 'High',
        category: q.valueNodeCategory || 'Strategy',
        estimatedImpact: undefined,
        selected: q.priority === 'Critical' || index < 5,
        dataSources: Array.isArray(q.dataSources) ? q.dataSources : [],
      }));

      // Replace this persona's questions rather than appending to them.
      // Appending stacked a fresh set on top of the previous one every time a
      // persona was regenerated, so the list accumulated near-duplicates plus
      // stale questions from earlier runs (including ones generated before the
      // backend's company-grounding filter existed). Other personas' questions
      // are untouched.
      setQuestionsData([
        ...questionsData.filter(q => q.personaRole !== persona),
        ...newQuestions,
      ]);
      toast.success(`${newQuestions.length} questions generated for ${persona}`);

    } catch (error: any) {
      console.error(`❌ Failed to generate questions for ${persona}:`, error);
      toast.error(`Failed to generate questions for ${persona}: ${error.message || 'Unknown error'}`);
    } finally {
      setGeneratingForPersona(null);
    }
  };

  // Filter questions
  const filteredQuestions = questionsData.filter(q => {
    const personaMatch = selectedPersona === 'all' || q.personaRole === selectedPersona;
    const priorityMatch = selectedPriority === 'all' || q.priority === selectedPriority;
    return personaMatch && priorityMatch;
  });

  // Normalize persona role to match PERSONAS values (handles case variations)
  const normalizePersonaRole = (role: string | undefined): string => {
    if (!role) return 'Other';
    const upperRole = role.toUpperCase();
    // Check if it matches any persona value (case-insensitive)
    const matchedPersona = PERSONAS.find(p => p.value.toUpperCase() === upperRole);
    if (matchedPersona) return matchedPersona.value;
    // Check if it's a full title like "Chief Supply Chain Officer"
    const matchedByTitle = PERSONAS.find(p =>
      p.label.toLowerCase().includes(role.toLowerCase()) ||
      role.toLowerCase().includes(p.value.toLowerCase())
    );
    if (matchedByTitle) return matchedByTitle.value;
    return role; // Return as-is if no match found
  };

  // Group questions by persona
  const groupedQuestions = filteredQuestions.reduce((acc, question) => {
    const persona = normalizePersonaRole(question.personaRole);
    if (!acc[persona]) acc[persona] = [];
    acc[persona].push(question);
    return acc;
  }, {} as Record<string, QuestionData[]>);

  const selectedCount = questionsData.filter(q => q.selected).length;
  const criticalCount = questionsData.filter(q => q.selected && q.priority === 'Critical').length;

  const canProceed = () => {
    return selectedCount >= 3; // Require at least 3 questions
  };

  // Show persona selection screen first
  if (showPersonaSelection && questionsData.length === 0) {
    return (
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h2 className="text-3xl font-bold text-neutral-900">Strategic Questions</h2>
          <p className="text-neutral-600">
            Select C-level personas for {companyData?.name || 'your company'}
          </p>
          <p className="text-sm text-primary-600">
            ✨ AI will generate 4-5 strategic questions for each selected persona
          </p>
        </div>

        {/* Persona Selection Card */}
        <Card className="p-8">
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xl font-semibold text-neutral-900">Select Personas (2-3 recommended)</h3>
                <p className="text-sm text-neutral-600 mt-1">
                  Choose the C-level roles most relevant to your case
                </p>
              </div>
              <div className="text-sm text-neutral-600">
                {selectedPersonasForGen.length} selected
              </div>
            </div>

            {/* Persona Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
              {PERSONAS.map((persona) => {
                const isSelected = selectedPersonasForGen.includes(persona.value);
                return (
                  <button
                    key={persona.value}
                    onClick={() => {
                      if (isSelected) {
                        setSelectedPersonasForGen(selectedPersonasForGen.filter(p => p !== persona.value));
                      } else {
                        setSelectedPersonasForGen([...selectedPersonasForGen, persona.value]);
                      }
                    }}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-neutral-200 bg-white hover:border-primary-300'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-2xl">{persona.icon}</span>
                      <span className="font-semibold text-neutral-900">{persona.value}</span>
                    </div>
                    <p className="text-xs text-neutral-600">{persona.label.split('(')[1]?.replace(')', '')}</p>
                  </button>
                );
              })}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-between items-center pt-4 border-t">
              <Button variant="outline" onClick={onBack}>
                Back
              </Button>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowPersonaSelection(false);
                    setShowManualAdd(true);
                  }}
                >
                  Enter Manually
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    setShowPersonaSelection(false);
                    handleGenerateQuestions();
                  }}
                  disabled={selectedPersonasForGen.length === 0}
                  className="flex items-center gap-2"
                >
                  <Sparkles className="w-4 h-4" />
                  Generate Questions ({selectedPersonasForGen.length} personas)
                </Button>
              </div>
            </div>

            {selectedPersonasForGen.length > 3 && (
              <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <p className="text-sm text-amber-700">
                  Selecting 2-3 personas is recommended for faster generation and more focused questions
                </p>
              </div>
            )}
          </div>
        </Card>
      </div>
    );
  }

  if (isGenerating) {
    // Flow-wise skeleton — mirrors the loaded shape (one persona group with
    // ~4 question placeholder rows each) so the eventual real questions slot
    // into the same layout without a jarring shift. Each persona gets its
    // own card, each question slot has a pulsing title + body line.
    return (
      <div className="max-w-4xl mx-auto" aria-busy="true" aria-live="polite">
        <div className="space-y-3">
          {/* Header strip — keeps the user oriented while the AI works */}
          <Card className="p-4 bg-gradient-to-r from-primary-50 to-white border-primary-200">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                <Sparkles className="w-4 h-4 text-primary-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-neutral-900">
                  Generating strategic questions
                </h3>
                <p className="text-xs text-neutral-600">
                  Creating 4–5 questions for each of the{' '}
                  <span className="font-medium">{selectedPersonasForGen.length}</span>{' '}
                  persona{selectedPersonasForGen.length !== 1 ? 's' : ''}: {selectedPersonasForGen.join(', ')}
                </p>
              </div>
              <span className="text-xs text-neutral-500 whitespace-nowrap">~30–60s</span>
            </div>
            {/* Indeterminate bar */}
            <div className="h-1 w-full bg-neutral-100 rounded-full overflow-hidden mt-3">
              <div className="h-full w-1/3 bg-primary-500 rounded-full animate-pulse" />
            </div>
          </Card>

          {/* One placeholder card per persona with 2 compact question slots.
              Cut from 4 slots → 2 to keep the loader visible on one screen
              instead of stretching 12+ skeleton rows below the fold. */}
          {selectedPersonasForGen.map((persona) => (
            <Card key={persona} className="p-3">
              <div className="flex items-center gap-2.5 mb-2.5 animate-pulse">
                <div className="w-7 h-7 rounded-full bg-neutral-200" />
                <div className="space-y-1">
                  <div className="h-3 w-28 bg-neutral-200 rounded" />
                  <div className="h-2 w-16 bg-neutral-100 rounded" />
                </div>
              </div>
              <div className="space-y-1.5">
                {[0, 1].map((i) => (
                  <div
                    key={i}
                    className="flex items-start gap-2.5 p-2.5 border border-neutral-200 rounded-md bg-neutral-50 animate-pulse"
                    style={{ animationDelay: `${i * 120}ms` }}
                  >
                    <div className="w-3.5 h-3.5 mt-0.5 bg-neutral-200 rounded shrink-0" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-2.5 bg-neutral-200 rounded" style={{ width: `${72 + i * 12}%` }} />
                      <div className="h-2.5 bg-neutral-200 rounded" style={{ width: `${45 + i * 8}%` }} />
                      {/* Tag row inline */}
                      <div className="flex gap-1.5">
                        <div className="h-3 w-10 bg-neutral-200 rounded-full" />
                        <div className="h-3 w-14 bg-neutral-100 rounded-full" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          ))}

          {/* Friendly stay-put-or-leave note matching the analyze-all flow */}
          <div className="flex items-start gap-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg">
            <Sparkles className="w-3.5 h-3.5 text-blue-600 mt-0.5 shrink-0" />
            <p className="text-xs text-blue-900 leading-relaxed">
              Feel free to switch to another tab — we'll keep working in the background.
              The questions will appear here automatically when ready.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-bold text-neutral-900">Strategic Questions</h2>
        <p className="text-neutral-600">
          Amplinth-generated questions tailored for executive decision-making
        </p>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4 bg-gradient-to-br from-green-50 to-white border-green-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{selectedCount}</p>
              <p className="text-xs text-neutral-600">Questions Selected</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-red-50 to-white border-red-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{criticalCount}</p>
              <p className="text-xs text-neutral-600">Critical Priority</p>
            </div>
          </div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-blue-50 to-white border-blue-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-blue-600">{Object.keys(groupedQuestions).length}</p>
              <p className="text-xs text-neutral-600">Executive Roles</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Suggested-theme chips — clickable Industry Research signals
          (opportunity/risk areas, strategic imperatives, active disruptions)
          that seed manual questions via the existing setQuestionsData path.
          Hidden when no insights data is available. */}
      {industryData?.industryId && (
        <SuggestedThemeChips
          industryId={industryData.industryId}
          onAdd={(q) => setQuestionsData([...questionsData, q])}
        />
      )}

      {/* Action Buttons & Filters */}
      <div className="flex justify-between items-center gap-4">
        <div className="flex gap-3">
          <Button
            variant="outline"
            onClick={handleGenerateQuestions}
            disabled={isGenerating}
            className="flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Regenerate
          </Button>
          {questionsData.length > 0 && (
            <Button
              variant="outline"
              onClick={() =>
                setPendingConfirm({
                  title: 'Delete all questions?',
                  message:
                    'This clears every generated and manually added question. You will need to regenerate them.',
                  confirmLabel: 'Delete all',
                  onConfirm: () => {
                    clearQuestionsData();
                    toast.success('All questions deleted');
                  },
                })
              }
              className="flex items-center gap-2 text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
            >
              <X className="w-4 h-4" />
              Clear All
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setShowManualAdd(!showManualAdd)}
            className="flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Question
          </Button>
        </div>

        <div className="flex gap-3">
          <select
            value={selectedPersona}
            onChange={(e) => setSelectedPersona(e.target.value)}
            className="px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Roles</option>
            {PERSONAS.map(p => (
              <option key={p.value} value={p.value}>{p.icon} {p.label}</option>
            ))}
          </select>
          <select
            value={selectedPriority}
            onChange={(e) => setSelectedPriority(e.target.value)}
            className="px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="all">All Priorities</option>
            {PRIORITIES.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Persona Management */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-lg font-semibold text-neutral-900">C-Level Personas</h3>
          <p className="text-sm text-neutral-500">Click on a persona to generate questions</p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {PERSONAS.map(persona => {
            const questionCount = questionsData.filter(q => q.personaRole === persona.value).length;
            const hasQuestions = questionCount > 0;
            const isGenerating = generatingForPersona === persona.value;

            return (
              <div
                key={persona.value}
                className={`p-3 rounded-lg border transition-all ${
                  hasQuestions
                    ? 'border-primary-200 bg-primary-50'
                    : isGenerating
                    ? 'border-primary-400 bg-primary-100 animate-pulse'
                    : 'border-neutral-200 bg-neutral-50 hover:border-primary-300 hover:bg-primary-50 cursor-pointer'
                }`}
                onClick={() => {
                  if (!hasQuestions && !isGenerating && !generatingForPersona) {
                    handleGenerateForPersona(persona.value);
                  }
                }}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-lg">{persona.icon}</span>
                      <span className="font-semibold text-sm text-neutral-900">{persona.value}</span>
                    </div>
                    {isGenerating ? (
                      <div className="flex items-center gap-1 text-xs text-primary-600">
                        <Loader2 className="w-3 h-3 animate-spin" />
                        <span>Generating...</span>
                      </div>
                    ) : hasQuestions ? (
                      <p className="text-xs text-neutral-600 truncate">{questionCount} {questionCount === 1 ? 'question' : 'questions'}</p>
                    ) : (
                      <div className="flex items-center gap-1 text-xs text-primary-600">
                        <Plus className="w-3 h-3" />
                        <span>Click to add</span>
                      </div>
                    )}
                  </div>
                  {hasQuestions && !isGenerating && (
                    <div className="flex gap-1 flex-shrink-0">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingConfirm({
                            title: `Regenerate ${persona.value} questions?`,
                            message: `The ${questionCount} existing ${persona.value} ${questionCount === 1 ? 'question' : 'questions'} will be replaced with a freshly generated set.`,
                            confirmLabel: 'Regenerate',
                            onConfirm: () => {
                              handleRemovePersona(persona.value);
                              handleGenerateForPersona(persona.value);
                            },
                          });
                        }}
                        className="text-neutral-400 hover:text-primary-600 transition-colors"
                        title={`Regenerate ${persona.value} questions`}
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setPendingConfirm({
                            title: `Remove ${persona.value} questions?`,
                            message: `All ${questionCount} ${persona.value} ${questionCount === 1 ? 'question' : 'questions'} will be removed. Other roles are unaffected.`,
                            confirmLabel: 'Remove',
                            onConfirm: () => handleRemovePersona(persona.value),
                          });
                        }}
                        className="text-neutral-400 hover:text-red-600 transition-colors"
                        title={`Remove all ${persona.value} questions`}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Manual Add Form */}
      {showManualAdd && (
        <Card className="p-6 bg-neutral-50">
          <h3 className="text-lg font-semibold text-neutral-900 mb-4">Add Strategic Question</h3>
          <div className="space-y-4">
            <div className="grid md:grid-cols-3 gap-4">
              <select
                value={manualQuestion.personaRole}
                onChange={(e) => setManualQuestion({ ...manualQuestion, personaRole: e.target.value })}
                className="px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {PERSONAS.map(p => (
                  <option key={p.value} value={p.value}>{p.icon} {p.label}</option>
                ))}
              </select>
              <select
                value={manualQuestion.priority}
                onChange={(e) => setManualQuestion({ ...manualQuestion, priority: e.target.value as any })}
                className="px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {PRIORITIES.map(p => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
              <select
                value={manualQuestion.category}
                onChange={(e) => setManualQuestion({ ...manualQuestion, category: e.target.value })}
                className="px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {CATEGORIES.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <Input
              type="text"
              placeholder="Strategic Question *"
              value={manualQuestion.question}
              onChange={(e) => setManualQuestion({ ...manualQuestion, question: e.target.value })}
            />
            <Input
              type="text"
              placeholder="Context (optional)"
              value={manualQuestion.context}
              onChange={(e) => setManualQuestion({ ...manualQuestion, context: e.target.value })}
            />
            <div className="flex gap-2">
              <Button variant="primary" onClick={handleAddManualQuestion} disabled={!manualQuestion.question}>
                Add Question
              </Button>
              <Button variant="outline" onClick={() => setShowManualAdd(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* Per-persona generation loader. The persona chip up top only shows a
          tiny spinner; without a skeleton here the questions area gave no sign
          work was happening — for a regenerate, the persona's old questions were
          removed and the eye landed on the next persona's list or the empty
          state. This mirrors the full-regenerate skeleton so the loader is
          clearly visible right where the new questions will land. */}
      {generatingForPersona && (
        <div className="space-y-3" aria-busy="true" aria-live="polite">
          <Card className="p-4 border-primary-200 bg-gradient-to-r from-primary-50 to-white">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center shrink-0">
                <Sparkles className="w-4 h-4 text-primary-600" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-neutral-900">
                  Generating {generatingForPersona} questions
                </h3>
                <p className="text-xs text-neutral-600">
                  Creating 4–5 strategic questions. Any existing questions stay in place.
                </p>
              </div>
              <span className="text-xs text-neutral-500 whitespace-nowrap">~15–30s</span>
            </div>
            <div className="h-1 w-full bg-neutral-100 rounded-full overflow-hidden mt-3">
              <div className="h-full w-1/3 bg-primary-500 rounded-full animate-pulse" />
            </div>
          </Card>
          <div className="space-y-2">
            {[0, 1, 2].map((i) => (
              <Card key={i} className="p-4 animate-pulse">
                <div className="flex items-start gap-3">
                  <div className="w-4 h-4 mt-1 bg-neutral-200 rounded shrink-0" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 bg-neutral-200 rounded" style={{ width: `${70 + i * 8}%` }} />
                    <div className="h-3 bg-neutral-100 rounded" style={{ width: `${45 + i * 10}%` }} />
                    <div className="flex gap-2 pt-1">
                      <div className="h-4 w-14 bg-neutral-200 rounded-full" />
                      <div className="h-4 w-20 bg-neutral-100 rounded-full" />
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Questions by Persona */}
      {Object.keys(groupedQuestions).length > 0 ? (
        <div className="space-y-6">
          {Object.entries(groupedQuestions).map(([persona, questions]) => {
            const personaInfo = PERSONAS.find(p => p.value === persona);
            return (
              <div key={persona}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-2xl">{personaInfo?.icon || '👤'}</span>
                  <h3 className="text-xl font-semibold text-neutral-900">{persona}</h3>
                  <span className="text-sm text-neutral-500">({questions.length} questions)</span>
                </div>
                <div className="space-y-3">
                  {questions.map((question) => (
                    <Card
                      key={question.questionId}
                      className={`p-4 transition-all ${
                        question.selected
                          ? 'border-2 border-primary-500 bg-primary-50'
                          : 'border border-neutral-200 hover:shadow-md'
                      }`}
                    >
                      {editingQuestionId === question.questionId ? (
                        // Edit mode
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 mb-2">
                            <Edit2 className="w-4 h-4 text-primary-600" />
                            <span className="text-sm font-medium text-primary-600">Editing Question</span>
                          </div>
                          <textarea
                            value={editingQuestion?.question || ''}
                            onChange={(e) => setEditingQuestion(prev => prev ? {...prev, question: e.target.value} : null)}
                            className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                            rows={2}
                            placeholder="Strategic question..."
                          />
                          <input
                            type="text"
                            value={editingQuestion?.context || ''}
                            onChange={(e) => setEditingQuestion(prev => prev ? {...prev, context: e.target.value} : null)}
                            className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                            placeholder="Context/rationale (optional)"
                          />
                          <div className="flex gap-3">
                            <select
                              value={editingQuestion?.priority || 'High'}
                              onChange={(e) => setEditingQuestion(prev => prev ? {...prev, priority: e.target.value as any} : null)}
                              className="px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                              {PRIORITIES.map(p => (
                                <option key={p} value={p}>{p}</option>
                              ))}
                            </select>
                            <select
                              value={editingQuestion?.category || 'Strategy'}
                              onChange={(e) => setEditingQuestion(prev => prev ? {...prev, category: e.target.value} : null)}
                              className="px-3 py-2 border border-neutral-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                              {CATEGORIES.map(c => (
                                <option key={c} value={c}>{c}</option>
                              ))}
                            </select>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="primary"
                              size="sm"
                              onClick={() => handleSaveEdit(question.questionId!)}
                              disabled={!editingQuestion?.question}
                            >
                              <Check className="w-4 h-4 mr-1" />
                              Save
                            </Button>
                            <Button variant="outline" size="sm" onClick={handleCancelEdit}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // View mode
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={question.selected}
                            onChange={() => handleToggleQuestion(question.questionId!)}
                            className="mt-1 w-4 h-4 text-primary-600 rounded focus:ring-primary-500"
                          />
                          <div className="flex-1 space-y-2">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <p className="font-medium text-neutral-900 leading-relaxed">{question.question}</p>
                                {question.context && (
                                  <p className="text-sm text-neutral-600 mt-1">{question.context}</p>
                                )}
                              </div>
                              <div className="flex gap-1 ml-3">
                                <button
                                  onClick={() => handleStartEdit(question)}
                                  className="p-1 text-neutral-400 hover:text-primary-600 transition-colors"
                                  title="Edit question"
                                >
                                  <Edit2 className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleRemoveQuestion(question.questionId!)}
                                  className="p-1 text-neutral-400 hover:text-red-600 transition-colors"
                                  title="Remove question"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 flex-wrap">
                              <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                                question.priority === 'Critical' ? 'bg-red-100 text-red-700' :
                                question.priority === 'High' ? 'bg-orange-100 text-orange-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {question.priority}
                              </span>
                              {question.category && (
                                <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-neutral-100 text-neutral-700">
                                  {question.category}
                                </span>
                              )}
                              {question.estimatedImpact && (
                                <span className="text-xs text-neutral-600">
                                  💡 {question.estimatedImpact}
                                </span>
                              )}
                            </div>
                            {/* Provenance chips — Discovery data points the
                                persona agent used to anchor this question.
                                Renders only when the backend emitted them, so
                                older questions / manual ones look unchanged. */}
                            {question.dataSources && question.dataSources.length > 0 && (
                              <div className="flex items-center gap-1.5 flex-wrap pt-1">
                                <span className="text-[10px] uppercase tracking-wide text-neutral-400 font-semibold">
                                  Anchored in
                                </span>
                                {question.dataSources.map((ds, dsIdx) => (
                                  <span
                                    key={`${question.questionId}-ds-${dsIdx}`}
                                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded border ${DATA_SOURCE_PALETTE[ds.type] || DATA_SOURCE_PALETTE.default}`}
                                    title={`${ds.label}: ${ds.value}`}
                                  >
                                    <span className="opacity-70">{ds.label}:</span>
                                    <span className="font-semibold">{ds.value}</span>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : generatingForPersona ? null : (
        <Card className="p-12 text-center">
          <FileText className="w-16 h-16 mx-auto mb-4 text-neutral-300" />
          <h3 className="text-xl font-semibold text-neutral-700 mb-2">
            No questions generated
          </h3>
          <p className="text-neutral-500 mb-6">
            Generate Amplinth-powered strategic questions or add questions manually
          </p>
          <Button onClick={handleGenerateQuestions} variant="primary">
            Generate Questions
          </Button>
        </Card>
      )}

      {/* Requirement Notice */}
      {!canProceed() && questionsData.length > 0 && (
        <div className="flex items-center gap-2 p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-amber-600" />
          <p className="text-sm text-amber-800">
            Please select at least 3 strategic questions to proceed
          </p>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between pt-6 border-t border-neutral-200">
        <Button variant="outline" onClick={onBack}>
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back
        </Button>
        <Button
          variant="primary"
          onClick={onNext}
          disabled={!canProceed()}
          className="flex items-center gap-2"
        >
          Next: Review & Submit
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </Button>
      </div>

      {/* Themed replacement for window.confirm() — every destructive action on
          this step routes through this one dialog. */}
      <ConfirmDialog
        isOpen={pendingConfirm !== null}
        title={pendingConfirm?.title ?? ''}
        message={pendingConfirm?.message ?? ''}
        confirmLabel={pendingConfirm?.confirmLabel ?? 'Confirm'}
        variant="danger"
        onCancel={() => setPendingConfirm(null)}
        onConfirm={() => {
          pendingConfirm?.onConfirm();
          setPendingConfirm(null);
        }}
      />
    </div>
  );
};

export default Step4StrategicQuestions;
